import type { ComponentProps } from "react";
import type { Editor } from "@monaco-editor/react";

export const LINE_HEIGHT = 19;
export const MAX_LINES = 4;
export const PADDING_Y = 12;

/**
 * Monaco editor options configured to look like a simple text input.
 * Disables all IDE features: context menus, command palette, suggestions, etc.
 */
export const EDITOR_OPTIONS: ComponentProps<typeof Editor>["options"] = {
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  wordWrap: "on",
  lineNumbers: "off",
  renderLineHighlight: "none",
  scrollbar: {
    vertical: "hidden",
    horizontal: "hidden",
    handleMouseWheel: false,
    useShadows: false,
  },
  overviewRulerLanes: 0,
  hideCursorInOverviewRuler: true,
  overviewRulerBorder: false,
  folding: false,
  glyphMargin: false,
  lineDecorationsWidth: 0,
  lineNumbersMinChars: 0,
  renderWhitespace: "none",
  contextmenu: false,
  quickSuggestions: false,
  suggestOnTriggerCharacters: false,
  acceptSuggestionOnEnter: "off",
  tabCompletion: "off",
  wordBasedSuggestions: "off",
  parameterHints: { enabled: false },
  hover: { enabled: "off" },
  links: false,
  colorDecorators: false,
  codeLens: false,
  inlayHints: { enabled: "off" },
  lightbulb: { enabled: "off" as never },
  matchBrackets: "never",
  selectionHighlight: false,
  occurrencesHighlight: "off",
  renderLineHighlightOnlyWhenFocus: false,
  cursorBlinking: "solid",
  cursorStyle: "line",
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontSize: 14,
  lineHeight: LINE_HEIGHT,
  padding: { top: 6, bottom: 6 },
  automaticLayout: true,
  find: {
    addExtraSpaceOnTop: false,
    autoFindInSelection: "never",
    seedSearchStringFromSelection: "never",
  },
  unicodeHighlight: { ambiguousCharacters: false },
  bracketPairColorization: { enabled: false },
  guides: { indentation: false, bracketPairs: false },
  stickyScroll: { enabled: false },
  dropIntoEditor: { enabled: false },
  accessibilitySupport: "auto",
} as const;

// Chat History Constants
export const AI_EXPAND_SEARCH_PARAM = "ai-expand" as const;
export const AI_EXPAND_VALUE = "1" as const;

export const DEFAULT_SESSION_TITLE = "New chat";
export const SESSION_TITLE_MAX_INPUT_LENGTH = 100;

export const ACTIVE_THREAD_STORAGE_KEY = "definity-ai-active-thread";
export const THREADS_PAGE_SIZE = 50;
export const AI_THREADS_QUERY_KEY = ["ai-assistant", "threads"] as const;
export const AI_ASSISTANTS_QUERY_KEY = ["ai-assistant", "assistants"] as const;

/** Graph (assistant) ID in aegra.json that runs use unless debug mode picks another. */
export const DEFAULT_ASSISTANT_ID = "definity_claude_sdk";

export const AI_ASSISTANT_DISABLED_MESSAGE =
  "The AI assistant is turned off for your organization.";

export const MESSAGE_CONTEXT_KWARG = "definity_context";
export const MESSAGE_CONTEXT_URL_KWARG = "definity_context_url";

// Chat History Dropdown UI Constants
export const HISTORY_DROPDOWN_MAX_HEIGHT = 400;
export const HISTORY_DROPDOWN_WIDTH = 280;
export const HISTORY_DROPDOWN_EMPTY_MESSAGE = "No previous chats";

// Message Spacing Constants
export const MESSAGE_SPACER_GAP = 38;

// AI Context Label Constants
/** Context labels for compare runs page */
export const COMPARE_RUNS_CONTEXT_LABELS = {
  page: "Page",
  env1: "Base Environment",
  pipeline1: "Base Pipeline",
  pit1: "Base PIT",
  task1: "Base Task",
  env2: "Compare Environment",
  pipeline2: "Compare Pipeline",
  pit2: "Compare PIT",
  task2: "Compare Task",
  tab: "Tab",
} as const;

/** Base context labels (used by getContextLabels in utils) */
export const BASE_CONTEXT_LABELS = {
  page: "Page",
  env_name: "Env",
  app_name: "Pipeline",
  app_pit: "Pipeline PIT",
  task_id: "Task ID",
  tf_id: "Transformation ID",
  ds_name: "Dataset",
  timeWindow: "Time Window",
  appTags: "Tags",
  appOwners: "Owners",
  tab: "Tab",
} as const;
